import { string, date, mixed, setLocale } from 'yup';
import { subYears } from 'date-fns';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

var DOB_MAX_AGE = 100;
var DOB_MIN_AGE = 13;
var NAME_MAX_LENGTH = 100;
var NAME_REGEXP = /^[A-Za-z\u00c0-\u00ff\u0027]+(?:[ _-][A-Za-z\u00c0-\u00ff\u0027]+)*$/;
var PASSWORD_MIN_LENGTH = 8;
var HOUSENUMBER_MAX_LENGTH = 10;
var ZIPCODE_MAX_LENGTH = 10;
var TELEPHONE_REGEXP = /^(\+?[\d\-\s]+)$/;
var LINKED_IN_REGEX = /linkedin/i;
var NL_LOCALE = 'nl';
/* LABELS */

var LINKED_IN_LABEL = 'linked_in';

var currentYear = new Date().getFullYear();
var maxDateOfBirthNetherlands = subYears(new Date(currentYear, 0, 1), DOB_MIN_AGE - 1);
var maxDateOfBirthGeneral = subYears(new Date(), DOB_MIN_AGE);

var maxDateOfBirth = function maxDateOfBirth(locale) {
  var isDutchSite = locale === NL_LOCALE;
  return isDutchSite ? maxDateOfBirthNetherlands : maxDateOfBirthGeneral;
};

var validName = function validName() {
  return string().label('name').trim().max(NAME_MAX_LENGTH).matches(NAME_REGEXP);
};
var validCandidateAge = function validCandidateAge(locale) {
  return date().label('candidate_age').max(maxDateOfBirth(locale)).min(subYears(new Date(), DOB_MAX_AGE));
};
var validEndDate = function validEndDate(startDate) {
  return string().label('valid_end_date').test({
    name: 'validEndDate',
    test: function test(value) {
      // See https://github.com/jquense/yup/issues/423 for more info about resolve
      var startDateRef = this.resolve(startDate);
      var startTimestamp = Date.parse(startDateRef);
      var endTimestamp = Date.parse(value);
      var endDateAfterStartDate = endTimestamp >= startTimestamp;
      return startDateRef && value ? endDateAfterStartDate : true;
    }
  }).transform(function (value) {
    return value.toString();
  });
};
var validPassword = function validPassword() {
  return string().min(PASSWORD_MIN_LENGTH);
};
var validZipcode = function validZipcode() {
  return string().max(ZIPCODE_MAX_LENGTH);
};
var validHousenumber = function validHousenumber() {
  return string().max(HOUSENUMBER_MAX_LENGTH);
};
var validTelephone = function validTelephone() {
  return string().label('telephone').trim().matches(TELEPHONE_REGEXP);
};
var validMinimumAge = function validMinimumAge() {
  return mixed().label('minimum_age').oneOf([true]);
};
var validTermsResponse = function validTermsResponse() {
  return mixed().label('terms_agreement').oneOf([true]);
};
var validLinkedInUrl = function validLinkedInUrl() {
  return string().label(LINKED_IN_LABEL).url().matches(LINKED_IN_REGEX, {
    excludeEmptyString: true
  });
};
var validUniqueEmail = function validUniqueEmail(uniqueEmailChecker) {
  return string().email().label('email_not_unique').test({
    name: 'emailNotUnique',
    test: function test(email) {
      return uniqueEmailChecker(email);
    }
  });
};

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

var Context = function Context() {
  var _this = this;

  _classCallCheck(this, Context);

  this.instance = null;

  this.getInstance = function () {
    if (!_this.instance) {
      throw new ReferenceError('getInstance was called before setInstance');
    }

    return _this.instance;
  };

  this.setInstance = function (inst) {
    _this.instance = inst;
  };

  this.t = function () {
    var _this$getInstance;

    return (_this$getInstance = _this.getInstance()).t.apply(_this$getInstance, arguments);
  };
};

var _Context = new Context(),
    setInstance = _Context.setInstance,
    t = _Context.t;

function initLocales() {
  setLocale({
    string: {
      email: function email() {
        return t('email_invalid');
      },
      max: function max(_ref) {
        var _max = _ref.max;
        return t('text_too_long', {
          max: _max
        });
      },
      min: function min(_ref2) {
        var _min = _ref2.min;
        return t('text_too_short', {
          min: _min
        });
      },
      url: function url() {
        return t('url_invalid');
      },
      matches: function matches(_ref3) {
        var regex = _ref3.regex,
            label = _ref3.label;
        var type = label || 'text';
        return t("".concat(type, "_invalid"), {
          regex: regex
        });
      }
    },
    date: {
      min: function min(_ref4) {
        var label = _ref4.label,
            _min2 = _ref4.min;

        if (label === 'candidate_age') {
          return t('candidate_age_too_old', {
            maxAge: DOB_MAX_AGE
          });
        }

        return t('date_too_early', {
          min: _min2
        });
      },
      max: function max(_ref5) {
        var label = _ref5.label,
            _max2 = _ref5.max;

        if (label === 'candidate_age') {
          return t('candidate_age_too_young', {
            minAge: DOB_MIN_AGE
          });
        }

        return t('date_too_late', {
          max: _max2
        });
      }
    },
    mixed: {
      oneOf: function oneOf(_ref6) {
        var label = _ref6.label,
            values = _ref6.values;

        if (label === 'terms_agreement') {
          return t('terms_agreement');
        }

        if (label === 'minimum_age') {
          return t('minimum_age');
        }

        return t('must_be_one_of', {
          values: values
        });
      },
      default: function _default(_ref7) {
        var label = _ref7.label;

        if (label === 'valid_end_date') {
          return t('end_date_before_start_date');
        }

        if (label === 'email_not_unique') {
          return t('email_not_unique');
        }

        return t('invalid');
      },
      required: function required() {
        return t('required');
      }
    },
    array: {
      min: function min(_ref8) {
        var _min3 = _ref8.min;
        return t('items_too_few', {
          min: _min3
        });
      }
    }
  });
}

var candidate_age_too_old = "Bist du dir sicher, dass dein Alter stimmt? Es erscheint ein bisschen zu alt";
var candidate_age_too_young = "Du musst mindestens {{minAge}} Jahre alt sein.";
var date_format_invalid = "Du hast ein ungültiges Datum eingegeben, ein Datum muss folgendes Format haben: {{format}}";
var date_too_early = "Das Datum muss nach {{max}} sein.";
var date_too_late = "Das Datum sollte vor {{max}} sein.";
var email = "Diese E-Mail ist ungültig. Bitte gib eine gültige, funktionierende E-Mail-Adresse ein.";
var email_invalid = "Diese E-Mail ist ungültig. Bitte gib eine gültige, funktionierende E-Mail-Adresse ein.";
var email_not_unique = "Diese E-Mail Adresse wird bereits verwendet.";
var end_date_before_start_date = "Das Datum sollte nach dem Startdatum liegen.";
var invalid = "Diese Eingabe ist ungültig.";
var items_too_few = "Du solltest mindestens {{min}} Artikel auswählen.";
var linked_in_invalid = "Dies scheint keine gültige LinkedIn-URL zu sein.";
var minimum_age = "Eine Registrierung ist erst ab 16 möglich";
var must_be_one_of = "Dies sollte einer der folgenden Werte sein: {{values}}";
var name_invalid = "Der Name darf nur Buchstaben, Bindestriche und Leerzeichen enthalten";
var required = "Dies ist ein Pflichtfeld.";
var telephone_invalid = "Die Telefonnummer enthält ungültige Zeichen.";
var terms_agreement = "AGB und Nutzungsbedingungen sowie die Datenschutzerklärung müssen akzeptiert werden.";
var text_too_long = "Dieser Text ist zu lang. Es sollten maximal {{max}} Zeichen sein.";
var text_too_short = "Dieser Text ist zu kurz. Er sollte mindestens {{min}} Zeichen haben.";
var url_invalid = "Diese URL ist ungültig.";
var de = {
	candidate_age_too_old: candidate_age_too_old,
	candidate_age_too_young: candidate_age_too_young,
	date_format_invalid: date_format_invalid,
	date_too_early: date_too_early,
	date_too_late: date_too_late,
	email: email,
	email_invalid: email_invalid,
	email_not_unique: email_not_unique,
	end_date_before_start_date: end_date_before_start_date,
	invalid: invalid,
	items_too_few: items_too_few,
	linked_in_invalid: linked_in_invalid,
	minimum_age: minimum_age,
	must_be_one_of: must_be_one_of,
	name_invalid: name_invalid,
	required: required,
	telephone_invalid: telephone_invalid,
	terms_agreement: terms_agreement,
	text_too_long: text_too_long,
	text_too_short: text_too_short,
	url_invalid: url_invalid
};

var candidate_age_too_old$1 = "Are you sure your age is correct? It seems a bit high..";
var candidate_age_too_young$1 = "You need to be at least {{minAge}} years old.";
var date_format_invalid$1 = "You've entered an invalid date, a date has to have a following format: {{format}}";
var date_too_early$1 = "The date has to be after {{max}}.";
var date_too_late$1 = "The date should be before {{max}}.";
var email$1 = "This email is not valid. Please fill in a valid, working email address.";
var email_invalid$1 = "This email is not valid. Please fill in a valid, working email address.";
var email_not_unique$1 = "This e-mail is already in use.";
var end_date_before_start_date$1 = "The date should be after the starting date.";
var invalid$1 = "This input is invalid.";
var items_too_few$1 = "You should select at least {{min}} item(s).";
var linked_in_invalid$1 = "Does not appear to be a valid LinkedIn URL";
var minimum_age$1 = "You have to be older than 16";
var must_be_one_of$1 = "This should be one of the following values: {{values}}";
var name_invalid$1 = "Name may only contain letters, dashes and spaces.";
var required$1 = "This field is required.";
var telephone_invalid$1 = "The phone number contains invalid characters.";
var terms_agreement$1 = "The Privacy Statement has to be accepted.";
var text_too_long$1 = "This text is too long. It should be a maximum of {{max}} characters.";
var text_too_short$1 = "This text is too short. It should consist of a minimum of {{min}} characters. ";
var url_invalid$1 = "This URL is not valid.";
var en = {
	candidate_age_too_old: candidate_age_too_old$1,
	candidate_age_too_young: candidate_age_too_young$1,
	date_format_invalid: date_format_invalid$1,
	date_too_early: date_too_early$1,
	date_too_late: date_too_late$1,
	email: email$1,
	email_invalid: email_invalid$1,
	email_not_unique: email_not_unique$1,
	end_date_before_start_date: end_date_before_start_date$1,
	invalid: invalid$1,
	items_too_few: items_too_few$1,
	linked_in_invalid: linked_in_invalid$1,
	minimum_age: minimum_age$1,
	must_be_one_of: must_be_one_of$1,
	name_invalid: name_invalid$1,
	required: required$1,
	telephone_invalid: telephone_invalid$1,
	terms_agreement: terms_agreement$1,
	text_too_long: text_too_long$1,
	text_too_short: text_too_short$1,
	url_invalid: url_invalid$1
};

var candidate_age_too_old$2 = "¿Estás seguro de haber introducido correctamente tu edad? Parece ser un número muy alto...";
var candidate_age_too_young$2 = "Necesitas tener al menos {{minAge}} años.";
var date_format_invalid$2 = "Has introducido una fecha no válida, esta ha de tener el siguiente formato: {{format}} ";
var date_too_early$2 = "La fecha ha de ser después de {{max}}.";
var date_too_late$2 = "La fecha ha de ser antes de {{max}}.";
var email$2 = "Esta dirección de correo electrónico no es válida. Por favor introduce una dirección que esté operativa.";
var email_invalid$2 = "Esta dirección de correo electrónico no es válida. Por favor introduce una dirección de correo válida y operativa.";
var email_not_unique$2 = "Este email ya está en uso.";
var end_date_before_start_date$2 = "La fecha ha de ser después de la fecha de inicio.";
var invalid$2 = "Este elemento no es válido.";
var items_too_few$2 = "Debes seleccionar al menos {{min}} elemento/s.";
var linked_in_invalid$2 = "Parece que la URL de LinkedIn no es válida";
var must_be_one_of$2 = "Ha de ser uno de los siguientes valores:  {{values}}";
var name_invalid$2 = "El nombre solo puede contener letras, guiones y espacios.";
var required$2 = "Este campo es obligatorio.";
var telephone_invalid$2 = "El número de teléfono contiene caracteres que no son válidos.";
var terms_agreement$2 = "Los acuerdos de privacidad han de ser aceptados.";
var text_too_long$2 = "Este texto es demasiado largo. Debe contener un máximo de {{max}} caracteres.";
var text_too_short$2 = "Este texto es demasiado corto. Debe contener un mínimo de {{min}} caracteres.";
var url_invalid$2 = "Esta URL no es válida.";
var es = {
	candidate_age_too_old: candidate_age_too_old$2,
	candidate_age_too_young: candidate_age_too_young$2,
	date_format_invalid: date_format_invalid$2,
	date_too_early: date_too_early$2,
	date_too_late: date_too_late$2,
	email: email$2,
	email_invalid: email_invalid$2,
	email_not_unique: email_not_unique$2,
	end_date_before_start_date: end_date_before_start_date$2,
	invalid: invalid$2,
	items_too_few: items_too_few$2,
	linked_in_invalid: linked_in_invalid$2,
	must_be_one_of: must_be_one_of$2,
	name_invalid: name_invalid$2,
	required: required$2,
	telephone_invalid: telephone_invalid$2,
	terms_agreement: terms_agreement$2,
	text_too_long: text_too_long$2,
	text_too_short: text_too_short$2,
	url_invalid: url_invalid$2
};

var candidate_age_too_old$3 = "Êtes-vous sûr de votre âge? Il semble un peu élevé..";
var candidate_age_too_young$3 = "Vous devez avoir au moins {{minAge}} ans.";
var date_format_invalid$3 = "Vous avez entré une date non valide. La date devrait avoir le format suivant: {{format}}";
var date_too_early$3 = "La date doit être après le {{max}}.";
var date_too_late$3 = "La date devrait être avant le {{max}}.";
var email$3 = "Cet email n'est pas valide. Veuillez renseigner un email professionnel valide svp.";
var email_invalid$3 = "Cet email n'est pas valide. Veuillez renseigner une adresse email professionnelle valide svp.";
var email_not_unique$3 = "Cet e-mail est déjà utilisé.";
var end_date_before_start_date$3 = "La date  doit être après la date de début";
var invalid$3 = "Cette entrée n'est pas valide.";
var items_too_few$3 = "Vous devez sélectionner au moins {{min}} éléments.";
var linked_in_invalid$3 = "Il semble que l'URL de votre profile LinkedIn ne fonctionne pas";
var must_be_one_of$3 = "Cela devrait être une des valeurs suivantes: {{values}}";
var name_invalid$3 = "Le nom doit seulement contenir des lettres, tirets et espaces.";
var required$3 = "Ce champ est requis";
var telephone_invalid$3 = "Le numéro de téléphone contient des caractères invalides.";
var terms_agreement$3 = "La déclaration de confidentialité doit être acceptée.";
var text_too_long$3 = "Le texte est trop long. Il devrait contenir au maximum {{max}} caractères.";
var text_too_short$3 = "Ce texte un trop court. Il devrait contenir au moins {{min}} caractères.";
var url_invalid$3 = "Cet URL n'est pas valide.";
var fr = {
	candidate_age_too_old: candidate_age_too_old$3,
	candidate_age_too_young: candidate_age_too_young$3,
	date_format_invalid: date_format_invalid$3,
	date_too_early: date_too_early$3,
	date_too_late: date_too_late$3,
	email: email$3,
	email_invalid: email_invalid$3,
	email_not_unique: email_not_unique$3,
	end_date_before_start_date: end_date_before_start_date$3,
	invalid: invalid$3,
	items_too_few: items_too_few$3,
	linked_in_invalid: linked_in_invalid$3,
	must_be_one_of: must_be_one_of$3,
	name_invalid: name_invalid$3,
	required: required$3,
	telephone_invalid: telephone_invalid$3,
	terms_agreement: terms_agreement$3,
	text_too_long: text_too_long$3,
	text_too_short: text_too_short$3,
	url_invalid: url_invalid$3
};

var candidate_age_too_old$4 = "Sorry, uw leeftijd is te hoog.";
var candidate_age_too_young$4 = "Je moet op zijn minst {{minAge}} jaar oud zijn.";
var date_format_invalid$4 = "You've entered an invalid date, a date has to have a following format: {{format}}";
var date_too_early$4 = "De datum moet na {{max}} zijn.";
var date_too_late$4 = "De datum moet voor {{max}} zijn.";
var email$4 = "Dit e-mailadres is niet geldig, vul een geldig, werkend e-mailadres in.";
var email_invalid$4 = "Dit e-mailadres is niet geldig, vul een geldig, werkend e-mailadres in.";
var email_not_unique$4 = "Dit e-mailadres staat al in ons bestand.";
var end_date_before_start_date$4 = "De datum moet na de startdatum zijn.";
var invalid$4 = "This input is invalid";
var items_too_few$4 = "Je moet minstens {{min}} item(s) selecteren.";
var linked_in_invalid$4 = "Lijkt geen geldige LinkedIn url te zijn";
var minimum_age$2 = "Je moet 13 jaar of ouder zijn";
var must_be_one_of$4 = "Dit moet een van de volgende waarden zijn: {{values}}.";
var name_invalid$4 = "Naam mag alleen letters, streepjes en spaties bevatten.";
var required$4 = "Dit veld is verplicht.";
var telephone_invalid$4 = "Het telefoonnummer bevat ongeldige tekens.";
var terms_agreement$4 = "Privacyverklaring moet worden geaccepteerd.";
var text_too_long$4 = "Deze tekst is te lang. Deze mag uit maximaal {{max}} karakters bestaan.";
var text_too_short$4 = "Deze tekst is te kort. Deze moet uit minimaal {{min}} karakters bestaan.";
var url_invalid$4 = "Dit url is niet geldig.";
var nl = {
	candidate_age_too_old: candidate_age_too_old$4,
	candidate_age_too_young: candidate_age_too_young$4,
	date_format_invalid: date_format_invalid$4,
	date_too_early: date_too_early$4,
	date_too_late: date_too_late$4,
	email: email$4,
	email_invalid: email_invalid$4,
	email_not_unique: email_not_unique$4,
	end_date_before_start_date: end_date_before_start_date$4,
	invalid: invalid$4,
	items_too_few: items_too_few$4,
	linked_in_invalid: linked_in_invalid$4,
	minimum_age: minimum_age$2,
	must_be_one_of: must_be_one_of$4,
	name_invalid: name_invalid$4,
	required: required$4,
	telephone_invalid: telephone_invalid$4,
	terms_agreement: terms_agreement$4,
	text_too_long: text_too_long$4,
	text_too_short: text_too_short$4,
	url_invalid: url_invalid$4
};

var candidate_age_too_old$5 = "Är du säker på att du fyllt i rätt ålder? Den verkar vara ganska hög...";
var candidate_age_too_young$5 = "Du behöver vara minst {{minAge}} år gammal.";
var date_format_invalid$5 = "Du har skrivit in ett felaktigt datum, det bör ha följande format: {{format}}";
var date_too_early$5 = "Datumet måste vara efter {{max}}. ";
var date_too_late$5 = "Datumet bör vara innan {{max}}.";
var email$5 = "Den här email-adressen är inte giltig. Fyll i en giltig och fungerande email-adress.";
var email_invalid$5 = "Email-adressen är ogiltig. Fyll i en giltig och fungerande email-adress.";
var email_not_unique$5 = "Denna e-postadress används redan.";
var end_date_before_start_date$5 = "Datumet bör vara efter startdatumet.";
var invalid$5 = "Den här inmatningen är ogiltig.";
var items_too_few$5 = "Du bör göra minst {{min}} val.";
var linked_in_invalid$5 = "Denna LinkedIn URL verkar inte vara giltig.";
var must_be_one_of$5 = "Det här bör vara en/ett av följande värden: {{values}}";
var name_invalid$5 = "Namn får endast innehålla bokstäver, bindestreck och mellanslag. ";
var required$5 = "Det här är ett obligatoriskt fält.";
var telephone_invalid$5 = "Telefonnumret innehåller ogiltiga tecken.";
var terms_agreement$5 = "Du måste godkänna sekretesspolicyn.";
var text_too_long$5 = "Texten är för lång. Den bör bestå av max {{max}} tecken.";
var text_too_short$5 = "Texten är för kort. Den bör bestå av minst {{min}} tecken.";
var url_invalid$5 = "Denna URL är inte giltig.";
var sv = {
	candidate_age_too_old: candidate_age_too_old$5,
	candidate_age_too_young: candidate_age_too_young$5,
	date_format_invalid: date_format_invalid$5,
	date_too_early: date_too_early$5,
	date_too_late: date_too_late$5,
	email: email$5,
	email_invalid: email_invalid$5,
	email_not_unique: email_not_unique$5,
	end_date_before_start_date: end_date_before_start_date$5,
	invalid: invalid$5,
	items_too_few: items_too_few$5,
	linked_in_invalid: linked_in_invalid$5,
	must_be_one_of: must_be_one_of$5,
	name_invalid: name_invalid$5,
	required: required$5,
	telephone_invalid: telephone_invalid$5,
	terms_agreement: terms_agreement$5,
	text_too_long: text_too_long$5,
	text_too_short: text_too_short$5,
	url_invalid: url_invalid$5
};

/* eslint import/prefer-default-export: off */
var IS_PRODUCTION = process.env.NODE_ENV === 'production';
var initValidationsI18next = function initValidationsI18next() {
  return {
    type: '3rdParty',
    init: function init() {
      initLocales();
      var instance = i18next.createInstance().use(LanguageDetector);
      setInstance(instance);
      return instance.init({
        resources: {
          de: {
            translation: de
          },
          en: {
            translation: en
          },
          es: {
            translation: es
          },
          fr: {
            translation: fr
          },
          nl: {
            translation: nl
          },
          sv: {
            translation: sv
          }
        },
        detection: {
          order: ['htmlTag'],
          caches: ['localStorage']
        },
        fallbackLng: ['en', 'dev'],
        interpolation: {
          formatSeparator: ','
        },
        returnObjects: true,
        debug: !IS_PRODUCTION
      });
    }
  };
};

export { initValidationsI18next, validName, validCandidateAge, validEndDate, validPassword, validZipcode, validHousenumber, validTelephone, validMinimumAge, validTermsResponse, validLinkedInUrl, validUniqueEmail, DOB_MAX_AGE, DOB_MIN_AGE, NAME_MAX_LENGTH, NAME_REGEXP, PASSWORD_MIN_LENGTH, HOUSENUMBER_MAX_LENGTH, ZIPCODE_MAX_LENGTH, TELEPHONE_REGEXP, LINKED_IN_REGEX, NL_LOCALE, LINKED_IN_LABEL };
